/* eslint-disable eqeqeq */
import {
  Dropdown,
  Form,
  FormInstance,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tooltip,
} from "antd";
import styles from "./index.less";
import { ReactNode, useMemo, useRef, useState } from "react";
import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
  ModuleTemplateTypeEnum,
} from "@packages/flow-core/module";
import { useFlow } from "../FlowEdit/useFlow";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import { ToolTargetHandle } from "../ToolHandle";
import useToken from "antd/es/theme/useToken";
import { ReactComponent as MoreIcon } from "../../../assets/more.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.svg";
import { ReactComponent as CopyIcon } from "../../../assets/copy.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.svg";
import { deepEqual } from "../../utils/deepEqual";
import { appModule2FlowNode } from "../../utils/adapt";
import { nanoid } from "nanoid";
import classNames from "classnames";
import { ItemType } from "antd/es/menu/interface";

export type NodeCardProps = FlowModuleItemType & {
  children?: ReactNode | ReactNode[] | string;
  selected?: boolean;
  forbitMenu?: boolean;
  minWidth?: number | string;
};

type RenameFormData = {
  name: string;
};
type RenameModalProps = {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: RenameFormData) => void;
  data?: RenameFormData;
};
function RenameModal({ visible, onClose, onConfirm, data }: RenameModalProps) {
  const ref = useRef<FormInstance<RenameFormData>>(null);
  return (
    <Modal
      open={visible}
      cancelText="取消"
      okText="保存"
      title="自定义标题"
      onCancel={onClose}
      onOk={() => ref.current?.submit()}
      destroyOnClose
    >
      <Form
        initialValues={data}
        ref={ref}
        onFinish={(values) => {
          onConfirm(values);
        }}
      >
        <Form.Item label="标题" name="name">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
}

const bgColors = {
  [ModuleTemplateTypeEnum.getContent]: "#f4edd1",
  [ModuleTemplateTypeEnum.output]: "#e1ecdf",
  [ModuleTemplateTypeEnum.tools]: "#e5edf4",
  [ModuleTemplateTypeEnum.flowControl]: "l#e4e6fb",
  [ModuleTemplateTypeEnum.logicDeal]: "#faece6",
  [ModuleTemplateTypeEnum.appCall]: "#f3edfa",
  [ModuleTemplateTypeEnum.enterpriseWeChat]: "#dcecf1",
  [ModuleTemplateTypeEnum.damselfish]: "#f5eedf",
} as Record<any, string>;
export default function NodeCard(props: NodeCardProps) {
  const {
    selected,
    name,
    avatar,
    children,
    forbitMenu,
    description: intro,
    moduleId,
    isTool = false,
    templateType,
  } = props;
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState<RenameFormData>();
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    copyModule,
    deleteModule,
    getAllNodes,
    updateModule,
    templates,
    delEdge,
    getAllEdges,
    addNodes,
    useablePlugins,
  } = useFlow();

  // 被删除的插件
  const isDeletedPluginModule = (() => {
    if (props.flowType !== FlowNodeTypeEnum.pluginModule) {
      return false;
    }

    const pluginValue =
      props.flowType === FlowNodeTypeEnum.pluginModule &&
      (props.inputs.find(
        (input) => input.key === ModuleInputKeyEnum.selectPlugin
      )?.value as string);

    if (!pluginValue) {
      return false;
    }

    if ((!useablePlugins || useablePlugins.length === 0) && !!pluginValue) {
      return true;
    }

    return !useablePlugins
      ?.map(
        (p) =>
          p.inputs.find(
            (input) => input.key === ModuleInputKeyEnum.selectPlugin
          )?.value
      )
      ?.includes(pluginValue);
  })();

  const showToolHandle = useMemo(() => {
    return (
      isTool &&
      !!getAllNodes().find(
        (node) => node.data.flowType === FlowNodeTypeEnum.tools
      )
    );
  }, [getAllNodes]);

  const canSync = useMemo(() => {
    const template = templates?.find(
      (item) => item.flowType === props.flowType
    );
    if (!template) {
      return false;
    }

    if (
      template.flowType === FlowNodeTypeEnum.pluginInput ||
      template.flowType === FlowNodeTypeEnum.pluginOutput ||
      template.flowType === FlowNodeTypeEnum.invokeApp
    ) {
      return false;
    }

    const currentInputs = props.inputs.filter((item) => !item.edit);
    const newInputs = template.inputs.filter((item) => !item.edit);
    const currentOutputs = (props.outputs || []).filter((item) => !item.edit);
    const newOutputs = (template.outputs || []).filter((item) => !item.edit);

    if (
      newInputs.length !== currentInputs.length ||
      newOutputs.length !== currentOutputs.length
    ) {
      return true;
    }

    return (
      newInputs.some((newItem, index) => {
        const oldItem = currentInputs[index];
        return (
          newItem.isTool != oldItem.isTool ||
          newItem.isTool != oldItem.isTool ||
          newItem.key !== oldItem.key ||
          newItem.label !== oldItem.label ||
          !deepEqual(newItem.list, oldItem.list) ||
          !deepEqual(newItem.editField, oldItem.editField) ||
          newItem.placeholder !== oldItem.placeholder ||
          newItem.showTargetInApp != oldItem.showTargetInApp ||
          newItem.showTargetInPlugin != oldItem.showTargetInPlugin ||
          newItem.valueType !== oldItem.valueType
        );
      }) ||
      newOutputs.some((newItem, index) => {
        const oldItem = currentOutputs[index];
        return (
          newItem.key !== oldItem.key ||
          newItem.label !== oldItem.label ||
          newItem.valueType !== oldItem.valueType
        );
      })
    );
  }, [templates, props.inputs, props.outputs]);

  const dropDownMenuItems: ItemType[] = isDeletedPluginModule
    ? [
        {
          label: "删除",
          icon: <DeleteIcon />,
          key: "delete",
          onClick() {
            deleteModule(moduleId);
          },
        },
      ]
    : [
        {
          label: "重命名",
          icon: <EditIcon />,
          key: "edit",
          onClick() {
            setOpen(true);
            setEditData({ name });
          },
        },
        {
          label: "复制",
          icon: <CopyIcon />,
          key: "copy",
          onClick() {
            copyModule(moduleId);
          },
        },
        {
          label: "删除",
          icon: <DeleteIcon />,
          key: "delete",
          onClick() {
            deleteModule(moduleId);
          },
        },
      ];

  const [, token] = useToken();

  function handleSync() {
    getAllEdges().forEach((edge) => {
      if (edge.source === moduleId || edge.target === moduleId) {
        delEdge(edge.id);
      }
    });
    const template = templates?.find(
      (item) => item.flowType === props.flowType
    );
    if (!template) {
      return;
    }
    const nodes = getAllNodes();
    const currentNode = nodes.find((item) => item.data.moduleId === moduleId);
    deleteModule(moduleId);
    addNodes(
      appModule2FlowNode({
        item: {
          ...template,
          moduleId: nanoid(),
          position: currentNode?.position,
        },
      })
    );
  }

  return (
    <>
      <div
        className={styles.container}
        style={{
          borderColor: isDeletedPluginModule
            ? "#D9D9D9"
            : selected
            ? token.colorPrimary
            : "#D9D9D9",
          minWidth: props.minWidth,
        }}
        ref={containerRef}
      >
        <div
          className={styles["header-bg"]}
          style={{
            background: `linear-gradient(180deg, ${
              bgColors[templateType] || "#f5eedf"
            } 0%,rgba(255, 255, 255, 0.70) 100%)`,
            filter: isDeletedPluginModule ? "grayscale(100%)" : "none",
          }}
        ></div>
        {showToolHandle && <ToolTargetHandle moduleId={moduleId} />}
        <div className={styles.header}>
          <Row justify="space-between">
            <div className={styles["header-layout"]}>
              <img
                className={
                  isDeletedPluginModule
                    ? classNames(styles.icon, styles.gray)
                    : styles.icon
                }
                src={avatar}
              />
              <span className={styles.name}>{name}</span>
              {isDeletedPluginModule && (
                <div className={styles.deleted}>已删除</div>
              )}
              {/* {isDeletedSelectedApp && <ErrorAlertIcon />} */}
            </div>
            <Space size={12}>
              {canSync && (
                <Tooltip title="">
                  <Popconfirm
                    title="同步版本将会重置该节点所有信息"
                    onConfirm={handleSync}
                    okText="确定"
                    cancelText="取消"
                  >
                    <RefreshIcon className={styles["action-btn"]} />
                  </Popconfirm>
                </Tooltip>
              )}
              {!forbitMenu && (
                <Dropdown
                  placement="bottomLeft"
                  menu={{ items: dropDownMenuItems }}
                  getPopupContainer={(node) => containerRef.current || node}
                  rootClassName={styles.dropdown}
                >
                  <MoreIcon className={styles["action-btn"]} />
                </Dropdown>
              )}
            </Space>
          </Row>
          <div className={styles.desc}>{intro}</div>
        </div>
        {children}
      </div>
      <RenameModal
        visible={open}
        data={editData}
        onClose={() => setOpen(false)}
        onConfirm={(values) => {
          setOpen(false);
          updateModule(moduleId, values);
        }}
      />
    </>
  );
}
